import React, { useEffect, useState } from "react";
import { NavBar, SideNav } from "../../components";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Modal,
  TextInput,
  Label,
  Badge,
  Spinner,
  Table,
  Tabs,
  Alert,
} from "flowbite-react";
import {
  fetchPendingPayouts,
  triggerPayout,
  fetchReferralSettings,
  updateReferralSettings,
  fetchAmbassadorRewards,
} from "../../redux/slices/referrals";
import { fetchPayouts, reversePayout } from "../../redux/slices/payouts";

const formatCurrency = (amount) =>
  `₦${Number(amount || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { dateStyle: "medium" }) : "—";

// ─── Pending Payouts Tab ─────────────────────────────────────────────────────

const PendingPayoutsTab = () => {
  const dispatch = useDispatch();
  const { pendingPayouts, pendingLoading, actionLoading, ambassadorRewards, rewardsLoading } = useSelector(
    (state) => state.referrals
  );
  const [confirmAmbassador, setConfirmAmbassador] = useState(null);
  const [note, setNote] = useState("");
  const [rewardsAmbassador, setRewardsAmbassador] = useState(null);

  useEffect(() => {
    dispatch(fetchPendingPayouts());
  }, [dispatch]);

  const handlePayout = () => {
    dispatch(
      triggerPayout({ ambassadorId: confirmAmbassador.ambassadorId, note })
    ).then((res) => {
      if (!res.error) {
        setConfirmAmbassador(null);
        setNote("");
      }
    });
  };

  const openRewards = (entry) => {
    setRewardsAmbassador(entry);
    dispatch(fetchAmbassadorRewards(entry.ambassadorId));
  };

  if (pendingLoading) {
    return (
      <div className="flex justify-center mt-10">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <>
      {pendingPayouts.length === 0 ? (
        <Alert color="info" className="mt-4">
          No pending payouts at the moment.
        </Alert>
      ) : (
        <Table hoverable className="mt-4">
          <Table.Head>
            <Table.HeadCell>Creator</Table.HeadCell>
            <Table.HeadCell>Referrals</Table.HeadCell>
            <Table.HeadCell>Pending Amount</Table.HeadCell>
            <Table.HeadCell>Oldest Pending</Table.HeadCell>
            <Table.HeadCell>Action</Table.HeadCell>
          </Table.Head>
          <Table.Body>
            {pendingPayouts.map((entry) => (
              <Table.Row key={entry.ambassadorId} className="bg-white dark:bg-gray-800">
                <Table.Cell>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {entry.ambassadorDisplayName || entry.ambassadorEmail}
                  </div>
                  {entry.ambassadorDisplayName && (
                    <div className="text-xs text-gray-500">{entry.ambassadorEmail}</div>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <button
                    className="text-blue-600 hover:underline font-medium"
                    onClick={() => openRewards(entry)}
                  >
                    {entry.pendingReferralCount}
                  </button>
                </Table.Cell>
                <Table.Cell className="font-semibold text-green-600">
                  {formatCurrency(entry.totalPendingAmount)}
                </Table.Cell>
                <Table.Cell className="text-xs text-gray-500">
                  {formatDate(entry.oldestPendingAt)}
                </Table.Cell>
                <Table.Cell>
                  <Button
                    size="xs"
                    onClick={() => setConfirmAmbassador(entry)}
                    disabled={actionLoading}
                  >
                    Mark as Paid
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {/* Rewards breakdown modal */}
      <Modal size="3xl" show={!!rewardsAmbassador} onClose={() => setRewardsAmbassador(null)}>
        <Modal.Header>
          Payments — {rewardsAmbassador?.ambassadorDisplayName || rewardsAmbassador?.ambassadorEmail}
        </Modal.Header>
        <Modal.Body>
          {rewardsLoading ? (
            <div className="flex justify-center py-6"><Spinner size="lg" /></div>
          ) : ambassadorRewards.length === 0 ? (
            <Alert color="info">No payment records found.</Alert>
          ) : (
            <Table>
              <Table.Head>
                <Table.HeadCell>Date</Table.HeadCell>
                <Table.HeadCell>Referee</Table.HeadCell>
                <Table.HeadCell>Payment</Table.HeadCell>
                <Table.HeadCell>Commission</Table.HeadCell>
                <Table.HeadCell>Status</Table.HeadCell>
              </Table.Head>
              <Table.Body>
                {ambassadorRewards.map((r) => (
                  <Table.Row key={r.rewardId} className="bg-white dark:bg-gray-800">
                    <Table.Cell className="text-xs text-gray-500 whitespace-nowrap">
                      {formatDate(r.createdAt)}
                    </Table.Cell>
                    <Table.Cell className="text-xs text-gray-500">{r.refereeEmail}</Table.Cell>
                    <Table.Cell>{formatCurrency(r.paymentAmount)}</Table.Cell>
                    <Table.Cell className="font-semibold text-green-600">
                      {formatCurrency(r.rewardAmount)}
                    </Table.Cell>
                    <Table.Cell>
                      <Badge color={r.clawedBackAt ? "failure" : r.rewardStatus === "paid" ? "success" : "warning"}>
                        {r.clawedBackAt ? "Refunded" : r.rewardStatus === "paid" ? "Paid" : "Pending"}
                      </Badge>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={!!confirmAmbassador} onClose={() => setConfirmAmbassador(null)}>
        <Modal.Header>Confirm Payout</Modal.Header>
        <Modal.Body>
          <p className="mb-4">
            Mark{" "}
            <span className="font-semibold">
              {confirmAmbassador?.ambassadorDisplayName || confirmAmbassador?.ambassadorEmail}
            </span>{" "}
            as paid?
          </p>
          <p className="text-lg font-bold text-green-600 mb-4">
            {formatCurrency(confirmAmbassador?.totalPendingAmount)}
          </p>
          <p className="text-sm text-gray-500 mb-2">
            This covers {confirmAmbassador?.pendingReferralCount} reward(s). The
            actual transfer must be done manually.
          </p>
          <Label htmlFor="payoutNote">Note (optional)</Label>
          <TextInput
            id="payoutNote"
            placeholder="e.g. Bank transfer ref #123"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handlePayout} disabled={actionLoading}>
            {actionLoading ? <Spinner size="sm" /> : "Confirm Payout"}
          </Button>
          <Button color="gray" onClick={() => setConfirmAmbassador(null)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// ─── Payout History Tab ──────────────────────────────────────────────────────

const PayoutHistoryTab = () => {
  const dispatch = useDispatch();
  const { payouts, pageLoading, actionLoading } = useSelector(
    (state) => state.payouts
  );
  const [reverseTarget, setReverseTarget] = useState(null);
  const [reverseReason, setReverseReason] = useState("");

  useEffect(() => {
    dispatch(fetchPayouts());
  }, [dispatch]);

  const handleReverse = () => {
    dispatch(
      reversePayout({ payoutId: reverseTarget.payoutId, reason: reverseReason })
    ).then((res) => {
      if (!res.error) {
        setReverseTarget(null);
        setReverseReason("");
      }
    });
  };

  if (pageLoading) {
    return (
      <div className="flex justify-center mt-10">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <>
      {payouts.length === 0 ? (
        <Alert color="info" className="mt-4">
          No payouts recorded yet.
        </Alert>
      ) : (
        <Table hoverable className="mt-4">
          <Table.Head>
            <Table.HeadCell>Date</Table.HeadCell>
            <Table.HeadCell>Creator</Table.HeadCell>
            <Table.HeadCell>Referrals</Table.HeadCell>
            <Table.HeadCell>Amount</Table.HeadCell>
            <Table.HeadCell>Triggered By</Table.HeadCell>
            <Table.HeadCell>Status</Table.HeadCell>
            <Table.HeadCell>Actions</Table.HeadCell>
          </Table.Head>
          <Table.Body>
            {payouts.map((payout) => (
              <Table.Row key={payout.payoutId} className="bg-white dark:bg-gray-800">
                <Table.Cell className="text-xs text-gray-500">
                  {formatDate(payout.createdAt)}
                </Table.Cell>
                <Table.Cell>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {payout.ambassadorDisplayName || payout.ambassadorEmail}
                  </div>
                  {payout.ambassadorDisplayName && (
                    <div className="text-xs text-gray-500">{payout.ambassadorEmail}</div>
                  )}
                </Table.Cell>
                <Table.Cell>{payout.referralCount}</Table.Cell>
                <Table.Cell className="font-semibold">
                  {formatCurrency(payout.totalAmount)}
                </Table.Cell>
                <Table.Cell className="text-xs text-gray-500">
                  {payout.triggeredByEmail}
                </Table.Cell>
                <Table.Cell>
                  <Badge color={payout.status === "reversed" ? "failure" : "success"}>
                    {payout.status === "reversed" ? "Reversed" : "Paid"}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  {payout.status === "active" && (
                    <Button
                      size="xs"
                      color="failure"
                      onClick={() => setReverseTarget(payout)}
                      disabled={actionLoading}
                    >
                      Reverse
                    </Button>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      <Modal show={!!reverseTarget} onClose={() => setReverseTarget(null)}>
        <Modal.Header>Reverse Payout</Modal.Header>
        <Modal.Body>
          <Alert color="warning" className="mb-4">
            Reversing this payout will reset all {reverseTarget?.referralCount} reward(s)
            back to "pending" so they can be paid again.
          </Alert>
          <Label htmlFor="reverseReason">Reason *</Label>
          <TextInput
            id="reverseReason"
            placeholder="Explain why this payout is being reversed"
            value={reverseReason}
            onChange={(e) => setReverseReason(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            color="failure"
            onClick={handleReverse}
            disabled={actionLoading || !reverseReason.trim()}
          >
            {actionLoading ? <Spinner size="sm" /> : "Reverse Payout"}
          </Button>
          <Button color="gray" onClick={() => setReverseTarget(null)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// ─── Settings Tab ────────────────────────────────────────────────────────────

const SettingsTab = () => {
  const dispatch = useDispatch();
  const { settings, settingsLoading } = useSelector((state) => state.referrals);
  const [percentage, setPercentage] = useState("");

  useEffect(() => {
    dispatch(fetchReferralSettings());
  }, [dispatch]);

  useEffect(() => {
    if (settings.rewardPercentage) {
      setPercentage(String(settings.rewardPercentage));
    }
  }, [settings.rewardPercentage]);

  const handleSave = () => {
    const value = parseFloat(percentage);
    if (!value || value <= 0 || value > 100) return;
    dispatch(updateReferralSettings({ rewardPercentage: value }));
  };

  return (
    <div className="mt-4 max-w-md">
      <Alert color="warning" className="mb-6">
        Changing the reward percentage only affects <strong>future payments</strong>.
        Past rewards are locked at the rate they were earned.
      </Alert>

      <div className="mb-4">
        <Label htmlFor="rewardPercentage">Reward Percentage (%)</Label>
        <div className="flex items-center gap-3 mt-1">
          <TextInput
            id="rewardPercentage"
            type="number"
            min="1"
            max="100"
            step="0.5"
            value={percentage}
            onChange={(e) => setPercentage(e.target.value)}
            className="w-32"
          />
          <span className="text-gray-500 text-sm">% of each referred payment</span>
        </div>
        {settings.lastUpdatedAt && (
          <p className="text-xs text-gray-400 mt-2">
            Last updated {formatDate(settings.lastUpdatedAt)} by{" "}
            {settings.lastUpdatedBy}
          </p>
        )}
      </div>

      <Button onClick={handleSave} disabled={settingsLoading}>
        {settingsLoading ? <Spinner size="sm" /> : "Save"}
      </Button>
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────

const Referrals = () => {
  return (
    <div>
      <NavBar />
      <SideNav />
      <main className="p-4 md:ml-64 h-auto pt-20">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
          Referrals
        </h1>

        <Tabs>
          <Tabs.Item title="Pending Payouts">
            <PendingPayoutsTab />
          </Tabs.Item>
          <Tabs.Item title="Payout History">
            <PayoutHistoryTab />
          </Tabs.Item>
          <Tabs.Item title="Settings">
            <SettingsTab />
          </Tabs.Item>
        </Tabs>
      </main>
    </div>
  );
};

export default Referrals;
